import { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, BadgeCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";

const ProductQA = ({ productId }) => {
  const { user, token } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [askMode, setAskMode] = useState(false);
  const [askBody, setAskBody] = useState("");
  const [submittingQ, setSubmittingQ] = useState(false);
  const [activeAnswerQ, setActiveAnswerQ] = useState(null);
  const [answerBody, setAnswerBody] = useState("");
  const [submittingA, setSubmittingA] = useState(false);

  const fetchQA = async () => {
    try {
      const r = await axios.get(`${API}/products/${productId}/qa`);
      setQuestions(r.data.questions || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQA();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const submitQuestion = async (e) => {
    e.preventDefault();
    if (!askBody.trim()) return;
    setSubmittingQ(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/products/${productId}/qa`, { body: askBody }, { headers });
      toast.success("Question posted!");
      setAskBody("");
      setAskMode(false);
      fetchQA();
    } catch {
      toast.error("Failed to post question");
    } finally {
      setSubmittingQ(false);
    }
  };

  const submitAnswer = async (e, questionId) => {
    e.preventDefault();
    if (!answerBody.trim()) return;
    setSubmittingA(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/products/${productId}/qa/${questionId}/answers`, { body: answerBody }, { headers });
      toast.success("Answer posted");
      setAnswerBody("");
      setActiveAnswerQ(null);
      fetchQA();
    } catch {
      toast.error("Failed to post answer");
    } finally {
      setSubmittingA(false);
    }
  };

  if (loading) return null;

  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mt-6" data-testid="product-qa-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-wide flex items-center gap-2" data-testid="qa-heading">
            <MessageSquare className="w-5 h-5 text-[#D62828]" />
            QUESTIONS & ANSWERS
          </h2>
          <p className="text-gray-500 text-sm mt-1">{questions.length} question{questions.length === 1 ? '' : 's'} so far</p>
        </div>
        {user ? (
          !askMode && (
            <button
              onClick={() => setAskMode(true)}
              className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-5 py-2 rounded-sm text-sm font-bold uppercase tracking-wide"
              data-testid="ask-question-btn"
            >
              Ask a Question
            </button>
          )
        ) : (
          <Link to="/login" className="text-[#D62828] hover:text-white text-sm uppercase tracking-wide font-bold">
            Log in to ask →
          </Link>
        )}
      </div>

      {user && askMode && (
        <form onSubmit={submitQuestion} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5 mb-6" data-testid="ask-question-form">
          <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">Your question</label>
          <textarea
            value={askBody}
            onChange={(e) => setAskBody(e.target.value)}
            rows={3}
            required
            placeholder="e.g. Will this compound work on chrome plating?"
            className="w-full bg-[#1A1A1A] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:border-[#D62828] focus:outline-none"
            data-testid="ask-question-input"
          />
          <div className="flex gap-3 mt-3">
            <button
              type="submit"
              disabled={submittingQ}
              className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-2 rounded-sm text-xs font-bold uppercase tracking-wide disabled:opacity-50"
              data-testid="submit-question-btn"
            >
              {submittingQ ? "Posting..." : "Post Question"}
            </button>
            <button type="button" onClick={() => setAskMode(false)} className="text-gray-400 hover:text-white text-xs px-3 py-2">Cancel</button>
          </div>
        </form>
      )}

      {questions.length === 0 ? (
        <p className="text-gray-500 text-sm">No questions yet. Be the first to ask!</p>
      ) : (
        <div className="space-y-5" data-testid="qa-list">
          {questions.map((q) => (
            <div key={q.question_id} className="border-b border-white/5 pb-5 last:border-0" data-testid={`qa-${q.question_id}`}>
              <div className="mb-2">
                <p className="text-xs text-gray-400 mb-1">
                  <span className="text-gray-500">Q:</span> {q.user_name} · {new Date(q.created_at).toLocaleDateString()}
                </p>
                <p className="text-white text-sm font-medium">{q.body}</p>
              </div>
              {(q.answers || []).length > 0 && (
                <div className="ml-4 mt-3 space-y-2">
                  {q.answers.map((a) => (
                    <div key={a.answer_id} className="bg-[#0A0A0A] border-l-2 border-[#D62828]/40 pl-3 py-2">
                      <p className="text-xs text-gray-400 mb-1 flex items-center gap-2">
                        <span className="text-gray-500">A:</span> {a.user_name}
                        {a.is_official && (
                          <span className="inline-flex items-center gap-0.5 text-[#D4AF37]">
                            <BadgeCheck className="w-3 h-3" /> Cut Above Team
                          </span>
                        )}
                        <span className="text-gray-500">· {new Date(a.created_at).toLocaleDateString()}</span>
                      </p>
                      <p className="text-gray-200 text-sm">{a.body}</p>
                    </div>
                  ))}
                </div>
              )}
              {user && (
                activeAnswerQ === q.question_id ? (
                  <form onSubmit={(e) => submitAnswer(e, q.question_id)} className="mt-3 ml-4" data-testid={`answer-form-${q.question_id}`}>
                    <textarea
                      value={answerBody}
                      onChange={(e) => setAnswerBody(e.target.value)}
                      rows={2}
                      required
                      placeholder="Share your experience..."
                      className="w-full bg-[#1A1A1A] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:border-[#D62828] focus:outline-none"
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        type="submit"
                        disabled={submittingA}
                        className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-4 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wide disabled:opacity-50"
                        data-testid={`submit-answer-${q.question_id}`}
                      >
                        Post Answer
                      </button>
                      <button type="button" onClick={() => { setActiveAnswerQ(null); setAnswerBody(""); }} className="text-gray-400 text-xs px-3 py-1.5">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <button
                    onClick={() => { setActiveAnswerQ(q.question_id); setAnswerBody(""); }}
                    className="text-[#D62828] hover:text-white text-xs uppercase tracking-wide font-bold mt-2 ml-4"
                    data-testid={`answer-btn-${q.question_id}`}
                  >
                    + Answer this
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductQA;
